import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from 'src/app/pipes/translate.pipe';
import { PushNotificationService } from 'src/app/shared/services/push-notification.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { SupabaseUserToolsService } from 'src/app/shared/services/supabase-user-tools.service';
import { UserService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-notification-settings',
  imports: [
    MatSlideToggleModule,
    ToggleSwitchModule,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    CommonModule,
  ],
  templateUrl: './notification-settings.component.html',
  styleUrl: './notification-settings.component.scss',
})
export class NotificationSettingsComponent implements OnInit {
  constructor(
    private snackBarService: SnackBarService,
    private pushNotificationsService: PushNotificationService,
    private translateService: TranslationService,
    private supabaseUserToolsService: SupabaseUserToolsService,
    private userService: UserService
  ) {}

  arePushNotificationsEnabled: boolean = false;
  permission: NotificationPermission = 'default';
  isSendingTest: boolean = false;

  async ngOnInit(): Promise<void> {
    if (typeof Notification !== 'undefined') {
      this.permission = Notification.permission;
    }

    if (this.permission === 'denied') {
      this.arePushNotificationsEnabled = false;
      return;
    }

    const userId = this.userService.currentUser?.id;
    if (userId) {
      // Check backend for existing subscription
      this.arePushNotificationsEnabled =
        await this.supabaseUserToolsService.isUserSubscribedToPushNotifications(
          userId
        );
    } else {
      this.arePushNotificationsEnabled = false;
    }
  }

  async toggleNotifications(togglePush: boolean) {
    if (this.permission === 'denied') {
      this.snackBarService.openSnackBar(
        this.translateService.translate(
          'You have denied notification permission for this browser. Please check browser settings.'
        )
      );
      // Ensure toggle stays off if denied
      setTimeout(() => (this.arePushNotificationsEnabled = false), 0);
      return;
    }

    if (togglePush) {
      try {
        await this.pushNotificationsService.subscribeToNotifications();

        // After attempt, re-verify state
        const userId = this.userService.currentUser?.id;
        if (userId) {
          this.arePushNotificationsEnabled =
            await this.supabaseUserToolsService.isUserSubscribedToPushNotifications(
              userId
            );
        }

        if (!this.arePushNotificationsEnabled) {
          throw new Error('VERIFICATION_FAILED');
        }
      } catch (err) {
        console.error('Subscription error in component:', err);
        const errorCode = err instanceof Error ? err.message : '';
        let message: string;

        switch (errorCode) {
          case 'PERMISSION_DENIED':
            message = this.translateService.translate(
              'Notifications are blocked. Please enable them in your browser or device settings and try again.'
            );
            break;
          case 'PERMISSION_DISMISSED':
            message = this.translateService.translate(
              'You need to allow notifications when prompted to enable this feature.'
            );
            break;
          case 'SW_SUBSCRIPTION_FAILED':
            message = this.translateService.translate(
              'Could not connect to the notification service. Please close and reopen the app, then try again.'
            );
            break;
          default:
            message = this.translateService.translate(
              'Something went wrong. Please try again later.'
            );
            break;
        }

        this.snackBarService.openSnackBar(message);
        // Force toggle back to off
        this.arePushNotificationsEnabled = false;
      }
    } else {
      try {
        await this.pushNotificationsService.unsubscribeFromNotifications();
        this.arePushNotificationsEnabled = false;
      } catch (err) {
        console.error('Unsubscribe error:', err);
        // Even if backend fails, we usually want to show UI as off,
        // but we'll re-check backend to be sure
        const userId = this.userService.currentUser?.id;
        if (userId) {
          this.arePushNotificationsEnabled =
            await this.supabaseUserToolsService.isUserSubscribedToPushNotifications(
              userId
            );
        }
      }
    }
  }

  async sendTestNotification() {
    const userId = this.userService.currentUser?.id;
    if (!userId) {
      this.snackBarService.openSnackBar(
        this.translateService.translate('Something went wrong. Please try again later')
      );
      return;
    }

    this.isSendingTest = true;
    try {
      const result = await this.pushNotificationsService.sendPush(
        this.translateService.translate('Test Notification'),
        this.translateService.translate('Push notifications are working correctly!'),
        false,
        [userId]
      );
      // Only a push FCM accepted counts. With this tab open it shows here as a
      // banner; in the background it shows as a system notification.
      this.snackBarService.openSnackBar(
        this.translateService.translate(
          result && result.sent > 0 ? 'Test notification sent!' : 'Failed to send test notification'
        )
      );
    } catch (err) {
      console.error('Test notification failed:', err);
      this.snackBarService.openSnackBar(
        this.translateService.translate('Failed to send test notification')
      );
    } finally {
      this.isSendingTest = false;
    }
  }
}
